import { supabase } from '../lib/supabase';
import { Participant } from '../types/models';

export const getParticipants = async (): Promise<Participant[]> => {
  const { data, error } = await supabase
    .from('students')
    .select(`
      student_id,
      first_name,
      last_name,
      first_name_tamil,
      last_name_tamil,
      date_of_birth,
      gender,
      house_id,
      department_id,
      reference_number,
      payment_status,
      payment_date,
      payment_notes,
      created_at,
      updated_at,
      created_by,
      updated_by,
      family_id,
      house:houses(
        house_id,
        house_name,
        house_color,
        created_at
      ),
      department:departments(
        department_id,
        name,
        name_tamil
      ),
      family:families(
        family_id,
        family_name,
        father_name,
        mother_name,
        guardian_name,
        guardian_relationship
      )
    `)
    .order('last_name');

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
};

export const getParticipantById = async (id: string): Promise<Participant | null> => {
  try {
    const { data, error } = await supabase
      .from('students')
      .select(`
        student_id,
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        gender,
        house_id,
        department_id,
        reference_number,
        payment_status,
        payment_date,
        payment_notes,
        created_at,
        updated_at,
        created_by,
        updated_by,
        family_id,
        house:houses(
          house_id,
          house_name,
          house_color,
          created_at
        ),
        department:departments(
          department_id,
          name,
          name_tamil
        ),
        family:families(
          family_id,
          family_name,
          father_name,
          mother_name,
          guardian_name,
          guardian_relationship
        ),
        registrations:student_event_registrations(
          registration_id,
          status,
          competition_year,
          event:events(
            event_id,
            event_name,
            gender_category,
            status,
            category:sports_categories(
              category_name
            )
          )
        )
      `)
      .eq('student_id', id)
      .maybeSingle();
      
    if (error) {
      throw new Error(error.message);
    }
    
    return data;
  } catch (error: any) {
    if (error.message.includes('PGRST116')) {
      return null;
    }
    throw error;
  }
};

export const createParticipant = async (participant: Omit<Participant, 'id' | 'created_at'>): Promise<Participant> => {
  // Check if user has admin, manager, or registration role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || 
    ur.roles.role_name === 'manager' ||
    ur.roles.role_name === 'registrering'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators, managers, and registration users can create participants');
  }

  const { data, error } = await supabase
    .from('students')
    .insert([participant])
    .select(`
      student_id,
      first_name,
      last_name,
      first_name_tamil,
      last_name_tamil,
      date_of_birth,
      gender,
      house_id,
      department_id,
      reference_number,
      payment_status,
      payment_date,
      payment_notes,
      created_at,
      updated_at,
      created_by,
      updated_by,
      house:houses(
        house_id,
        house_name,
        house_color,
        created_at
      ),
      department:departments(
        department_id,
        name,
        name_tamil
      )
    `)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  if (!data) {
    throw new Error('Failed to create participant');
  }
  
  return data;
};

export const updateParticipant = async (id: string, participant: Partial<Omit<Participant, 'id' | 'created_at'>>): Promise<Participant> => {
  // Check if user has admin, manager, or registration role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || 
    ur.roles.role_name === 'manager' ||
    ur.roles.role_name === 'registrering'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators, managers, and registration users can update participants');
  }

  const { data, error } = await supabase
    .from('students')
    .update(participant)
    .eq('student_id', id)
    .select(`
      student_id,
      first_name,
      last_name,
      first_name_tamil,
      last_name_tamil,
      date_of_birth,
      gender,
      house_id,
      department_id,
      reference_number,
      payment_status,
      payment_date,
      payment_notes,
      created_at,
      updated_at,
      created_by,
      updated_by,
      house:houses(
        house_id,
        house_name,
        house_color,
        created_at
      ),
      department:departments(
        department_id,
        name,
        name_tamil
      )
    `)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Failed to update participant');
  }
  
  return data;
};

export const deleteParticipant = async (id: string): Promise<void> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || 
    ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can delete participants');
  }

  const { error } = await supabase
    .from('students')
    .delete()
    .eq('student_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const getParticipantResults = async (participantId: string) => {
  const { data, error } = await supabase
    .from('competition_results')
    .select(`
      result_id,
      event_id,
      student_id,
      competition_year,
      position,
      points_earned,
      created_at,
      birth_year,
      age_group_id,
      verified_by,
      event:events(
        event_id,
        event_name,
        gender_category,
        is_team_event,
        points_allocation,
        created_at,
        status,
        min_birth_year,
        max_birth_year,
        age_group_id,
        competition_year,
        reference_number,
        verification_code,
        category:sports_categories(
          category_id,
          category_name,
          description,
          is_active,
          created_at
        )
      )
    `)
    .eq('student_id', participantId);
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const getParticipantsByGender = async (gender: string): Promise<Participant[]> => {
  let query = supabase
    .from('students')
    .select(`
      student_id,
      first_name,
      last_name,
      first_name_tamil,
      last_name_tamil,
      date_of_birth,
      gender,
      house_id,
      department_id,
      reference_number,
      payment_status,
      payment_date,
      payment_notes,
      created_at,
      updated_at,
      created_by,
      updated_by,
      house:houses(
        house_id,
        house_name,
        house_color,
        created_at
      ),
      department:departments(
        department_id,
        name,
        name_tamil
      )
    `)
    .order('last_name');

  // Only apply gender filter if not 'All'
  if (gender !== 'All') {
    query = query.eq('gender', gender === 'Male' ? 'M' : 'F');
  }
    
  const { data, error } = await query;
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const checkUserCanManageParticipants = async (): Promise<boolean> => {
  const { data: userRoles } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  return userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || 
    ur.roles.role_name === 'manager' ||
    ur.roles.role_name === 'registrering'
  ) ?? false;
};